/**
 * @fileoverview API client for command parser endpoints
 */

import apiClient from './client.js';

/**
 * Parse a natural language command into structured action (intent-based)
 * @param {string} command - The natural language command
 * @param {Object} options - Optional context and settings
 * @param {Object} options.context - Context (current item, issue, user)
 * @param {boolean} options.disableCache - Bypass intent cache for fresh responses
 * @returns {Promise<Object>} Parsed command with intent and parameters
 */
export const parseCommand = async (command, options = {}) => {
  const { context = {}, disableCache = false } = options;
  const response = await apiClient.post('/api/commands/parse', {
    command,
    context,
    disableCache
  });
  return response.data;
};

/**
 * Parse a natural language command using tool-based approach
 * @param {string} command - The natural language command
 * @param {Object} options - Optional context and settings
 * @param {Object} options.context - Context (current item, issue, user)
 * @param {boolean} options.disableCache - Bypass intent cache for fresh responses
 * @returns {Promise<Object>} Parsed command with tools and parameters
 */
export const parseCommandTools = async (command, options = {}) => {
  const { context = {}, disableCache = false } = options;
  const response = await apiClient.post('/api/commands/parse-tools', {
    command,
    context,
    disableCache
  });
  return response.data;
};

/**
 * Parse a natural language command using multi-intent approach
 * @param {string} command - The natural language command
 * @param {Object} options - Optional context and settings
 * @param {Object} options.context - Context (current item, issue, user)
 * @param {boolean} options.disableCache - Bypass intent cache for fresh responses
 * @returns {Promise<Object>} Parsed command with multiple intents and shared parameters
 */
export const parseCommandMulti = async (command, options = {}) => {
  const { context = {}, disableCache = false } = options;
  const response = await apiClient.post('/api/commands/parse-multi', {
    command,
    context,
    disableCache
  });
  return response.data;
};

/**
 * Quick assessment of command support without full parsing
 * @param {string} command - The natural language command
 * @returns {Promise<Object>} Assessment with confidence score
 */
export const assessCommand = async (command) => {
  const response = await apiClient.post('/api/commands/assess', {
    command
  });
  return response.data;
};

/**
 * Get list of supported intents with descriptions and examples
 * @returns {Promise<Object>} Supported intents with metadata
 */
export const getSupportedIntents = async () => {
  const response = await apiClient.get('/api/commands/intents');
  return response.data;
};

/**
 * Check command parser service health
 * @returns {Promise<Object>} Health status and service info
 */
export const getCommandsHealth = async () => {
  const response = await apiClient.get('/api/commands/health');
  return response.data;
};